import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";

type AppRole = Database["public"]["Enums"]["app_role"];

interface AuthContextType {
  session: Session | null;
  user: User | null;
  role: AppRole | null;       // primary role (admin > brand > creator)
  roles: AppRole[];           // all roles
  loading: boolean;
  signOut: () => Promise<void>;
}

const ROLE_PRIORITY: Record<AppRole, number> = { admin: 3, brand: 2, creator: 1 };

function pickPrimaryRole(roles: AppRole[]): AppRole | null {
  if (roles.length === 0) return null;
  return roles.sort((a, b) => ROLE_PRIORITY[b] - ROLE_PRIORITY[a])[0];
}

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  role: null,
  roles: [],
  loading: true,
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchRoles = async (userId: string): Promise<AppRole[]> => {
    const { data } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId);
    return (data ?? []).map((r) => r.role);
  };

  // Auto-backfill profile data from auth metadata for existing users
  const backfillProfile = async (authUser: User, userRoles: AppRole[]) => {
    const meta = authUser.user_metadata || {};
    const fullName = meta.full_name || meta.name || "";
    const avatarUrl = meta.avatar_url || meta.picture || null;
    const userEmail = authUser.email || "";

    if (!fullName && !avatarUrl) return; // nothing to backfill

    if (userRoles.includes("creator")) {
      const { data: profile } = await supabase
        .from("creator_profiles")
        .select("full_name, profile_photo, payment_email")
        .eq("user_id", authUser.id)
        .maybeSingle();
      if (profile && (!profile.full_name || !profile.profile_photo || !profile.payment_email)) {
        await supabase.from("creator_profiles").update({
          full_name: profile.full_name || fullName || null,
          profile_photo: profile.profile_photo || avatarUrl,
          payment_email: profile.payment_email || userEmail || null,
        }).eq("user_id", authUser.id);
      }
    }
    if (userRoles.includes("brand")) {
      const { data: profile } = await supabase
        .from("brand_profiles")
        .select("contact_person, logo_url")
        .eq("user_id", authUser.id)
        .maybeSingle();
      if (profile && (!profile.contact_person || !profile.logo_url)) {
        await supabase.from("brand_profiles").update({
          contact_person: profile.contact_person || fullName || null,
          logo_url: profile.logo_url || avatarUrl,
        }).eq("user_id", authUser.id);
      }
    }
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        setSession(newSession);
        setUser(newSession?.user ?? null);

        if (newSession?.user) {
          setTimeout(async () => {
            const r = await fetchRoles(newSession.user.id);
            setRoles(r);
            setLoading(false);
            // Backfill profile data from OAuth metadata if missing
            if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
              await backfillProfile(newSession.user, r);
            }
          }, 0);
        } else {
          setRoles([]);
          setLoading(false);
        }
      }
    );

    supabase.auth.getSession().then(async ({ data: { session: existing } }) => {
      setSession(existing);
      setUser(existing?.user ?? null);
      if (existing?.user) {
        const r = await fetchRoles(existing.user.id);
        setRoles(r);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    setSession(null);
    setUser(null);
    setRoles([]);
  };

  const role = pickPrimaryRole([...roles]);

  return (
    <AuthContext.Provider value={{ session, user, role, roles, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}
